import { toast } from 'react-toastify';

// Error types
export const ErrorTypes = {
  NETWORK: 'NETWORK_ERROR',
  AUTH: 'AUTH_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// Error messages
const ErrorMessages = {
  [ErrorTypes.NETWORK]: 'Network connection error. Please check your internet connection.',
  [ErrorTypes.AUTH]: 'Authentication error. Please log in again.',
  [ErrorTypes.AUTHORIZATION]: 'You don\'t have permission to perform this action.',
  [ErrorTypes.VALIDATION]: 'Please check your input and try again.',
  [ErrorTypes.SERVER]: 'Server error. Please try again later.',
  [ErrorTypes.UNKNOWN]: 'An unexpected error occurred. Please try again.'
};

// Error handler class
class ErrorHandler {
  static getErrorType(error) {
    if (!error.response) {
      return ErrorTypes.NETWORK;
    }

    const status = error.response.status;
    // 401: Unauthorized - Authentication failed (user not logged in or token expired)
    if (status === 401) {
      return ErrorTypes.AUTH;
    }
    // 403: Forbidden - User is authenticated but lacks permission for this resource
    if (status === 403) {
      return ErrorTypes.AUTHORIZATION;
    }
    if (status >= 500) {
      return ErrorTypes.SERVER;
    }
    if (status === 422) {
      return ErrorTypes.VALIDATION;
    }
    return ErrorTypes.UNKNOWN;
  }

  static getErrorMessage(error) {
    const errorType = this.getErrorType(error);
    
    // If server provides a specific message, use it
    if (error.response?.data) {
      return error.response.data;
    }
    
    // If server provides an array of errors, format them
    if (error.response?.data?.errors) {
      return Object.values(error.response.data.errors).flat().join(', ');
    }

    // Use default message based on error type
    return ErrorMessages[errorType];
  }

  static handleError(error, options = {}) {
    const {
      showToast = true,
      logToConsole = true,
      redirectTo = null,
      customMessage = null,
      onValidationError = null,
      onAuthError = null
    } = options;

    const errorType = this.getErrorType(error);
    const errorMessage = customMessage || this.getErrorMessage(error);

    // Log error to console if enabled
    if (logToConsole) {
      console.error(`[${errorType}]`, error);
    }

    // Show toast notification if enabled
    if (showToast) {
      console.log('Showing error toast:', errorMessage);
      toast.error(errorMessage, {
        position: "top-center", // Changed from "top-right" to "top-center" for mobile
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        // Mobile-friendly options
        limit: 3, // Limit number of toasts
        toastClassName: "mobile-toast", // Custom class for mobile styling
        bodyClassName: "mobile-toast-body", // Custom body class
        progressClassName: "mobile-toast-progress", // Custom progress class
        // Additional mobile optimizations
        pauseOnFocusLoss: false, // Prevent pausing on mobile
        rtl: false,
        newestOnTop: true,
      });
    }

    // Handle validation errors
    if (errorType === ErrorTypes.VALIDATION && onValidationError) {
      const validationErrors = error.response?.data?.errors || {};
      onValidationError(validationErrors);
    }

    // Handle auth errors (only 401 - authentication failures)
    if (errorType === ErrorTypes.AUTH) {
      localStorage.removeItem('tokenWithUser');
      if (onAuthError) {
        onAuthError();
      } else if (redirectTo) {
        // Use the provided redirect callback
        redirectTo('/signin');
      }
    } else if (redirectTo) {
      // Use the provided redirect callback for other redirects
      redirectTo();
    }

    return {
      type: errorType,
      message: errorMessage,
      originalError: error
    };
  }
}

export default ErrorHandler; 